import { zodResolver } from '@hookform/resolvers/zod'
import { Box, Stack } from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'

import CustomButton from '@/components/inputs/CustomButton'
import { DEFAULT_INTERFACE_LANGUAGE } from '@/constants/interfaceLanguages'
import { USER_ROLES } from '@/constants/userRoles'
import { useAlertContext } from '@/contexts/AlertContext'
import { useAuth } from '@/features/authentication/contexts/AuthContext'
import type { Site } from '@/features/site/types/site'
import CountryAccessSection from '@/features/user/components/form/CountryAccessSection'
import DeleteUserButton from '@/features/user/components/form/DeleteUserButton'
import FormFieldErrorText from '@/features/user/components/form/FormFieldErrorText'
import ResetPasswordButton from '@/features/user/components/form/ResetPasswordButton'
import SecuritySection from '@/features/user/components/form/SecuritySection'
import UserAccountAccordion from '@/features/user/components/form/UserAccountAccordion'
import UserInterfaceAccordion from '@/features/user/components/form/UserInterfaceAccordion'
import UserRightsAccordion from '@/features/user/components/form/UserRightsAccordion'
import UserSecurityAccordion from '@/features/user/components/form/UserSecurityAccordion'
import { DEFAULT_MFA_SETTINGS } from '@/features/user/constants/mfa'
import { ERROR_NAMES, saveUser as apiSaveUser, updateMfa } from '@/features/user/endpoints/users'
import { useInvalidateUserQuery } from '@/features/user/hooks/useUserQuery'
import { getUserFormSchema } from '@/features/user/schemas'
import type { User, UserForm } from '@/features/user/types/user'
import { getNewPayloadToUpdateMfaSettings } from '@/features/user/utils/mfa'
import { errorHandler } from '@/utils/errorHandler'

import CredentialsSection from './form/CredentialsSection'
import InformationSection from './form/InformationSection'
import InterfaceSection from './form/InterfaceSection'
import RoleSection from './form/RoleSection'
import SiteAccessSection from './form/SiteAccessSection'

interface UserFormProps {
  sites: Site[]
  user?: Partial<User>
}

export const PartnerUserForm = ({ sites, user }: UserFormProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { loggedInUser } = useAuth()

  const form = useForm<UserForm>({
    mode: 'onBlur',
    defaultValues: {
      id: user?.id ?? '',
      username: user?.username ?? '',
      givenName: user?.givenName ?? '',
      familyName: user?.familyName ?? '',
      email: user?.email ?? '',
      phone: user?.phone ?? '',
      role: user?.role ?? undefined,
      allowedCountries: user?.allowedCountries ?? [],
      isRestricted: user?.isRestricted ?? false,
      allowedRoIds: user?.allowedRoIds ?? [],
      partnerCode: user?.partnerCode ?? loggedInUser?.partnerCode ?? undefined,
      uiLanguage: user?.uiLanguage ?? DEFAULT_INTERFACE_LANGUAGE.value,
      isActive: user?.isActive ?? true,
      mfaSettings: user?.mfaSettings ?? DEFAULT_MFA_SETTINGS,
    },
    resolver: zodResolver(getUserFormSchema(t)),
  })
  const { pushAlert } = useAlertContext()
  const invalidateUserQuery = useInvalidateUserQuery()

  const editMode = !!user

  useWatch({
    control: form.control,
  })

  async function onSubmit() {
    await prepareValuesForSubmission()
    saveUser()
  }

  function prepareValuesForSubmission(): Promise<void> {
    const partnerCodeToAssign = loggedInUser?.partnerCode

    if (!partnerCodeToAssign) {
      pushAlert({
        severity: 'error',
        message: t('user_form.form.partner_code_assignation_error', {
          partnerCode: partnerCodeToAssign,
          username: loggedInUser?.username,
        }),
      })

      return Promise.reject(new Error(t('user_form.form.partner_code_assignation_error')))
    }
    form.setValue('partnerCode', partnerCodeToAssign)

    // FIXME: reconsider what to do with numeralLanguage, for now it is the same as uiLanguage
    form.setValue('numeralLanguage', form.getValues().uiLanguage)
    return Promise.resolve()
  }

  const { mutate: saveUser, isPending } = useMutation({
    mutationFn: () => apiSaveUser(form.getValues()),
    onError: (error: any) => {
      if (error.response.data.name === ERROR_NAMES.EMAIL_EXISTS) {
        form.setError('email', {
          type: 'alreadyExists',
          message: t('user_form.form.email_exists.error').toString(),
        })
      } else if (error.response.data.name === ERROR_NAMES.USERNAME_EXISTS) {
        form.setError('username', {
          type: 'alreadyExists',
          message: t('user_form.form.username_exists.error').toString(),
        })
      } else if (error.response.data.name === ERROR_NAMES.MFA_UPDATE_FAILED) {
        form.setError('mfaSettings', {
          type: 'updateFailed',
          message: t('user_form.form.mfa_update_failed.error').toString(),
        })
      } else if (
        error.response.data.name === ERROR_NAMES.VALIDATION_FAILED &&
        error.response.data.message.includes('phone')
      ) {
        form.setError('phone', {
          type: 'invalid',
          message: t('user_form.form.phone_invalid.error').toString(),
        })
      }
    },
    onSuccess: async (response: any) => {
      const mfaSettingsPayload = form.getValues('mfaSettings') ?? DEFAULT_MFA_SETTINGS

      // In case the user changed its mfaSettings, we need to call the API to update this information.
      if (JSON.stringify(user?.mfaSettings) !== JSON.stringify(mfaSettingsPayload)) {
        // The response.data is the user.id of the created/updated user.
        await updateMfa(response.data, getNewPayloadToUpdateMfaSettings(mfaSettingsPayload))

        await invalidateUserQuery()
      }

      navigate('/users')
    },
  })

  function handleResetPasswordError(err: unknown) {
    const error = errorHandler(err, t('user_form.reset_password.error'))

    pushAlert({
      severity: 'error',
      message: error.message,
    })
  }

  return (
    <FormProvider {...form}>
      <Stack
        noValidate
        aria-label={t('partner_user_form.form.title')}
        component="form"
        direction="column"
        gap={3}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <Box>
          <UserAccountAccordion user={user}>
            <CredentialsSection authenticationStatus={user?.authenticationStatus} editMode={editMode} />
            <InformationSection />
            {user?.id && editMode && (
              <Box>
                <ResetPasswordButton userId={user.id} onResetPasswordError={handleResetPasswordError} />
              </Box>
            )}
          </UserAccountAccordion>

          <UserRightsAccordion>
            <RoleSection
              roleOptions={[
                USER_ROLES.PARTNER_ADMINISTRATORS.value,
                USER_ROLES.RESOURCE_OWNERS.value,
                USER_ROLES.CUSTOMER_MANAGERS.value,
              ]}
            />
            <CountryAccessSection />
            <SiteAccessSection sites={sites} />
          </UserRightsAccordion>

          <UserSecurityAccordion preferredOption={user?.mfaSettings?.preferredOption ?? null}>
            <SecuritySection user={user ?? {}} />
            {form.formState.errors.mfaSettings && (
              <FormFieldErrorText message={form.formState.errors.mfaSettings.message} showIcon={true} />
            )}
          </UserSecurityAccordion>

          <UserInterfaceAccordion defaultExpanded={!editMode}>
            <InterfaceSection />
          </UserInterfaceAccordion>
        </Box>

        <Stack direction="row" gap={1} justifyContent="space-between">
          <Stack direction="row" gap={1} justifyContent="flex-start">
            <CustomButton component={Link} disabled={isPending} to="/users" variant="outlined">
              {t('common.button.cancel')}
            </CustomButton>
            <CustomButton disabled={isPending} type="submit" variant="contained">
              {t('common.button.save')}
            </CustomButton>
          </Stack>
          {editMode && <DeleteUserButton disabled={isPending} user={user} />}
        </Stack>
      </Stack>
    </FormProvider>
  )
}
